import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart2, Plus, X, CheckCircle2, Clock, Hash, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";

export default function Polls() {
  const { guildId, guild } = useGuild();
  const [items, setItems] = useState([]);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [channel, setChannel] = useState("");
  const [duration, setDuration] = useState(0);
  const locked = false;
  const load = () => api.get(`/guilds/${guildId}/polls`).then((r) => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [guildId]);

  const create = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question || opts.length < 2) return toast.error("Question + at least 2 options required");
    try { await api.post(`/guilds/${guildId}/polls`, { question, options: opts, channel, duration_minutes: Number(duration) || 0 }); toast.success(channel ? "Poll posted to Discord" : "Poll created"); setQuestion(""); setOptions(["", ""]); setChannel(""); setDuration(0); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const close = async (id) => { try { await api.post(`/guilds/${guildId}/polls/${id}/close`); toast.success("Poll closed"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Delete this poll?")) return; await api.delete(`/guilds/${guildId}/polls/${id}`); load(); };

  return (
    <div>
      <PageHead title="Polls" desc="Create polls and collect community votes." />
      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
          <h3 className="font-head font-semibold flex items-center gap-2"><BarChart2 className="w-4 h-4 text-volt" /> New poll</h3>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Question</label>
            <input data-testid="poll-question" value={question} onChange={(e) => setQuestion(e.target.value)} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
          <div className="space-y-2"><label className="text-xs uppercase tracking-wider text-zinc-500">Options</label>
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid="poll-option" value={o} onChange={(e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} placeholder={`Option ${i + 1}`} className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
                {options.length > 2 && <button onClick={() => setOptions(options.filter((_, x) => x !== i))} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            <button data-testid="poll-add-option" onClick={() => setOptions([...options, ""])} className="text-sm text-volt flex items-center gap-1"><Plus className="w-4 h-4" /> Add option</button>
          </div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Post to channel</label>
            <ChannelSelect guildId={guildId} value={channel} onChange={setChannel} testid="poll-channel" placeholder="— dashboard only (don't post) —" /></div>
          <div><label className="text-xs uppercase tracking-wider text-zinc-500">Time limit (minutes, 0 = none)</label>
            <input data-testid="poll-duration" type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" /></div>
          <button data-testid="poll-create" onClick={create} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Create poll</button>
        </div>
        <div className="lg:col-span-2 space-y-4">
          {items.map((p) => {
            const total = p.options.reduce((s, o) => s + o.votes, 0) || 1;
            return (
              <div key={p.id} data-testid="poll-row" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-head font-semibold">{p.question}</h4>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
                      {p.channel_id && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {p.channel || "Discord"}</span>}
                      {p.source === "discord" && <span className="text-volt">via /poll</span>}
                      {p.closes_at && p.status === "open" && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> closes {new Date(p.closes_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.status === "open" ? <button data-testid="poll-close" onClick={() => close(p.id)} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Close</button>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">closed</span>}
                    <button data-testid="poll-delete" onClick={() => del(p.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="space-y-2">
                  {p.options.map((o, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1"><span>{o.text}</span><span className="text-zinc-500">{Math.round((o.votes / total) * 100)}% · {o.votes}</span></div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-volt" style={{ width: `${(o.votes / total) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {items.length === 0 && <p className="text-zinc-600 text-sm">No polls yet.</p>}
        </div>
      </div>
    </div>
  );
}
